import { GetStaticProps } from 'next';
import Image from 'next/image';
import Link from 'next/link';
import styled, { css } from 'styled-components';

import theme from '../../assets/theme';
import { Button } from '../../components/_atoms/Button';
import MaxWidthContainer from '../../components/_atoms/MaxWidthContainer';
import LandingPageLayout from '../../components/layouts/LandingPageLayout';
import { DefaultLocale } from '../../providers/tolgee';

const MOBILE = '(max-width: 992px)';

interface Props {
  isAuthenticated: boolean;
}

const FlourBluffCaseStudy = ({ isAuthenticated }: Props) => {
  return (
    <LandingPageLayout
      title="Flour Bluff Elementary: From 6% to 49% in Top STAAR Quintiles | Curipod Case Study"
      subTitle="See how Curipod drives measurable student growth — from 6% to 49% in the top STAAR quintiles at Flour Bluff Elementary, Corpus Christi TX."
      disableHeadline={true}
    >
      {/* HERO */}
      <HeroSection>
        <MaxWidthContainer>
          <HeroContent>
            <HeroTag>CASE STUDY</HeroTag>
            <HeroTitle>From 6% to 49%</HeroTitle>
            <HeroSubtitle>Flour Bluff&apos;s STAAR Growth Story</HeroSubtitle>
            <HeroDescription>
              How a dedicated 4th-grade teacher, a curriculum supervisor, and
              Curipod&apos;s &ldquo;Flour Bluff&nbsp;&ndash; Small group&rdquo; agent
              transformed STAAR ELAR results and student confidence at Flour Bluff
              Elementary&nbsp;&mdash; Corpus Christi, Texas.
            </HeroDescription>
            <HeroMeta>
              <MetaBadge>Flour Bluff ISD</MetaBadge>
              <MetaBadge>4th Grade ELAR</MetaBadge>
              <MetaBadge>2024–2025 School Year</MetaBadge>
            </HeroMeta>
            <HeroCtaWrapper>
              <Link href={isAuthenticated ? '/' : '/signup'} passHref>
                <a>
                  <HeroCtaButton type="primary">Sign up for free</HeroCtaButton>
                </a>
              </Link>
            </HeroCtaWrapper>
          </HeroContent>
        </MaxWidthContainer>

        <StatBannerWrapper>
          <MaxWidthContainer>
            <StatBannerInner>
              <StatGroup>
                <StatItem>
                  <StatNumber>
                    6<StatPercent>%</StatPercent>
                  </StatNumber>
                  <StatLabel>Students in Top 40% STAAR</StatLabel>
                  <StatYear>3rd Grade ELAR · No Curipod</StatYear>
                </StatItem>
                <ArrowContainer>→</ArrowContainer>
                <StatItem $highlight>
                  <StatNumber $highlight>
                    49<StatPercent>%</StatPercent>
                  </StatNumber>
                  <StatLabel>Students in Top 40% STAAR</StatLabel>
                  <StatYear>4th Grade ELAR · With Curipod</StatYear>
                </StatItem>
              </StatGroup>

              <StatDivider />

              <StatGroup>
                <StatItem $highlight>
                  <StatNumber $highlight>
                    90<StatPercent>%</StatPercent>
                  </StatNumber>
                  <StatLabel>STAAR Passing Rate</StatLabel>
                  <StatYear>4th Grade ELAR · With Curipod</StatYear>
                </StatItem>
              </StatGroup>
            </StatBannerInner>
          </MaxWidthContainer>
        </StatBannerWrapper>
      </HeroSection>

      {/* DOWNLOAD CTA (below hero) */}
      <DownloadCtaBanner>
        <MaxWidthContainer>
          <DownloadCtaInner>
            <DownloadCtaLabel>
              Want the full story? Download the PDF version of this case study.
            </DownloadCtaLabel>
            <a
              href="/Curipod_Case_Study_Flour_Bluff_ISD.pdf"
              download="Curipod_Case_Study_Flour_Bluff_ISD.pdf"
            >
              <Button
                type="tertiary"
                styles={{ borderRadius: '20px', padding: '0 28px', height: 44 }}
              >
                Download PDF
              </Button>
            </a>
          </DownloadCtaInner>
        </MaxWidthContainer>
      </DownloadCtaBanner>

      {/* THE CHALLENGE */}
      <Section $bg={theme.colors.background}>
        <MaxWidthContainer>
          <TwoColumnGrid>
            <div>
              <SectionTag>A STRONG TEACHER IN A DEMANDING SETTING</SectionTag>
              <SectionTitle>Meeting Every Student Where They Are</SectionTitle>
              <SectionText>
                Flour Bluff Elementary in Corpus Christi, Texas is known for its academics
                and strong supports for students with special needs. The school serves a
                large SPED and dyslexic population, and many families move into the
                district specifically for its services.
              </SectionText>
              <SectionText>
                For 4th-grade ELAR and Social Studies teacher <strong>Lisl Ames</strong>,
                that means teaching students with a wide range of needs and behaviors
                within the structured, calm environment she is known for creating.
              </SectionText>
              <BlockQuote>
                <QuoteMark>&ldquo;</QuoteMark>
                <QuoteText>
                  We do have a very large SPED population and a large dyslexic group. The
                  need for differentiation there is massive.
                </QuoteText>
                <QuoteAttrib>— Lisl Ames, 4th Grade Teacher</QuoteAttrib>
              </BlockQuote>
            </div>

            <TeamCard>
              <TeamPhotoArea>
                <CircularImageWrap $size={150}>
                  <Image
                    src="/case-study-flour-bluff/classroom.jpg"
                    layout="fill"
                    objectFit="cover"
                    objectPosition="center"
                    alt="Flour Bluff classroom using Curipod"
                  />
                </CircularImageWrap>
                <TeamPhotoCaption>Flour Bluff 4th-grade classroom</TeamPhotoCaption>
              </TeamPhotoArea>
              <TeamCardTitle>The Flour Bluff Team</TeamCardTitle>
              <TeamMemberList>
                <TeamMember>
                  <TeamRole>4th Grade ELAR &amp; Social Studies Teacher</TeamRole>
                  <TeamName>Lisl Ames</TeamName>
                  <TeamNote>Structured, calm classroom environment</TeamNote>
                </TeamMember>
                <TeamMember>
                  <TeamRole>Elementary Curriculum Supervisor</TeamRole>
                  <TeamName>Nadine Lynch</TeamName>
                </TeamMember>
              </TeamMemberList>
            </TeamCard>
          </TwoColumnGrid>
        </MaxWidthContainer>
      </Section>

      {/* HOW CURIPOD MADE THE DIFFERENCE */}
      <Section $bg={theme.colors.paleBeige}>
        <MaxWidthContainer>
          <CenteredSection>
            <SectionTag style={{ display: 'block', textAlign: 'center' }}>
              FROM CHALLENGE TO CHANGE
            </SectionTag>
            <SectionTitle style={{ textAlign: 'center' }}>
              How Curipod Made the Difference
            </SectionTitle>
            <SectionText
              style={{ textAlign: 'center', maxWidth: 720, margin: '0 auto 40px' }}
            >
              When Ames first tried Curipod, she noticed how much smoother her lessons
              felt. Before, she pushed through long slide decks, and &ldquo;kids were
              glazed over.&rdquo; With Curipod, the structure keeps her students moving
              and attentive.
            </SectionText>

            <FocusAreaGrid>
              {[
                'Differentiation at Scale',
                'Immediate Feedback',
                'Small Group Instruction',
              ].map(area => (
                <FocusAreaBadge key={area}>{area}</FocusAreaBadge>
              ))}
            </FocusAreaGrid>

            <SectionText
              style={{ textAlign: 'center', maxWidth: 720, margin: '40px auto 0' }}
            >
              Curipod gave Ames more freedom as a teacher. Instead of being tied to the
              front of the room, she was free to circulate and connect with students.
            </SectionText>

            <BlockQuote style={{ maxWidth: 620, margin: '40px auto 0' }}>
              <QuoteMark>&ldquo;</QuoteMark>
              <QuoteText>
                It chunks everything out, which is so nice. It helps me structure my
                lessons. I&apos;m at every single desk and not as stagnant.
              </QuoteText>
              <QuoteAttrib>— Lisl Ames, 4th Grade Teacher</QuoteAttrib>
            </BlockQuote>
          </CenteredSection>
        </MaxWidthContainer>
      </Section>

      {/* THE CURIPOD SHIFT */}
      <Section $bg={theme.colors.background}>
        <MaxWidthContainer>
          <SectionTag style={{ display: 'block', textAlign: 'center' }}>
            THE CURIPOD SHIFT
          </SectionTag>
          <SectionTitle style={{ textAlign: 'center' }}>
            Four Ways Curipod Changed the Classroom
          </SectionTitle>
          <ImpactGrid>
            <ImpactCard>
              <ImpactIcon>💬</ImpactIcon>
              <ImpactTitle>Real-Time, Actionable Feedback</ImpactTitle>
              <ImpactQuote>
                &ldquo;They&apos;d revise based on the feedback. They gasped because their
                score went up. It&apos;s real time… instant.&rdquo;
              </ImpactQuote>
              <ImpactText>
                Ames had struggled to keep up with the cycle of collecting, grading, and
                returning student work. With Curipod, students didn&apos;t have to wait
                days for feedback — they could revise instantly.
              </ImpactText>
              <ImpactAttrib>— Lisl Ames</ImpactAttrib>
            </ImpactCard>

            <ImpactCard>
              <ImpactIcon>👥</ImpactIcon>
              <ImpactTitle>Powerful Small-Group Instruction</ImpactTitle>
              <ImpactQuote>
                &ldquo;It really does support that small group tutoring group. I can hone
                in even tighter… especially with the test prep.&rdquo;
              </ImpactQuote>
              <ImpactText>
                The same structure and immediate feedback that lifted whole-class
                instruction helped Ames target individual needs more efficiently in small
                groups with the &ldquo;Flour Bluff&nbsp;&ndash; Small group&rdquo; agent.
              </ImpactText>
              <ImpactAttrib>— Lisl Ames</ImpactAttrib>
            </ImpactCard>

            <ImpactCard>
              <ImpactIcon>🎯</ImpactIcon>
              <ImpactTitle>Quality Over Quantity</ImpactTitle>
              <ImpactQuote>
                &ldquo;They are more aware of the focus of the question… We&apos;re going
                for quality over quantity and no rambling.&rdquo;
              </ImpactQuote>
              <ImpactText>
                Students who once rambled through their responses or failed to finish
                began to focus with precision.
              </ImpactText>
              <ImpactAttrib>— Lisl Ames</ImpactAttrib>
            </ImpactCard>

            <ImpactCard>
              <ImpactIcon>📝</ImpactIcon>
              <ImpactTitle>Evidence-Based Writing</ImpactTitle>
              <ImpactQuote>
                &ldquo;They were much more specific with their evidence… &lsquo;in
                paragraph eight, it says…&rsquo;&rdquo;
              </ImpactQuote>
              <ImpactText>
                Students&apos; writing grew more disciplined and text-dependent over time.
                Even small wins felt huge.
              </ImpactText>
              <ImpactAttrib>
                — Nadine Lynch, Elementary Curriculum Supervisor
              </ImpactAttrib>
            </ImpactCard>
          </ImpactGrid>
        </MaxWidthContainer>
      </Section>

      {/* RESULTS */}
      <ResultsSection>
        <MaxWidthContainer>
          <CenteredSection>
            <SectionTag
              style={{
                color: theme.colors.orange,
                display: 'block',
                textAlign: 'center',
              }}
            >
              RESULTS YOU CAN SEE AND FEEL
            </SectionTag>
            <SectionTitle style={{ color: theme.colors.white, textAlign: 'center' }}>
              90% Passed the STAAR Test
            </SectionTitle>
            <SectionText
              style={{
                color: 'rgba(255,255,255,0.9)',
                textAlign: 'center',
                maxWidth: 720,
                margin: '0 auto 40px',
              }}
            >
              Both Ames and Lynch credit Curipod&apos;s immediate feedback and
              student-centered design for fueling that growth. A good portion of students
              who passed had not passed the year before.
            </SectionText>

            <ResultBlockQuote>
              <QuoteMark style={{ color: theme.colors.orange }}>&ldquo;</QuoteMark>
              <QuoteText style={{ color: theme.colors.white }}>
                A bunch of them got zeros the first time and everybody screamed when they
                got a one… it&apos;s the most tangible takeaway.
              </QuoteText>
              <QuoteAttrib style={{ color: theme.colors.orange }}>
                — Nadine Lynch, Elementary Curriculum Supervisor
              </QuoteAttrib>
            </ResultBlockQuote>

            <ResultNote>
              Students scoring in the top 40% for STAAR jumped from{' '}
              <strong>6% to 49%</strong> between 3rd-grade ELAR (no Curipod) and 4th-grade
              ELAR (with Curipod). That shift reflects focused instruction, real-time
              feedback, and a growing belief in their own ability.
            </ResultNote>

            <ResultsStudentsPhoto>
              <CircularImageWrap $size={160} $borderColor="rgba(249,151,70,0.6)">
                <Image
                  src="/case-study-flour-bluff/student-detail.jpg"
                  layout="fill"
                  objectFit="cover"
                  objectPosition="center top"
                  alt="Flour Bluff student working on Curipod"
                />
              </CircularImageWrap>
              <ResultsPhotoLabel>
                Flour Bluff student working with Curipod
              </ResultsPhotoLabel>
            </ResultsStudentsPhoto>
          </CenteredSection>
        </MaxWidthContainer>
      </ResultsSection>

      {/* WHY IT MATTERS */}
      <Section $bg={theme.colors.paleBeige}>
        <MaxWidthContainer>
          <CenteredSection>
            <SectionTag style={{ display: 'block', textAlign: 'center' }}>
              WHY IT MATTERS
            </SectionTag>
            <SectionTitle style={{ textAlign: 'center' }}>Why It Matters</SectionTitle>
            <SectionText
              style={{ textAlign: 'center', maxWidth: 720, margin: '0 auto 40px' }}
            >
              Curipod didn&apos;t change who Ames was as a teacher — it amplified her
              strengths. Automating the parts of teaching that drain time and energy gave
              her more space to do what she does best: connect with students and push them
              to succeed.
            </SectionText>

            <WhyItMattersGrid>
              <WhyItMattersCard>
                <WhyItMattersIcon>🌍</WhyItMattersIcon>
                <WhyItMattersTitle>For Diverse Learners</WhyItMattersTitle>
                <WhyItMattersText>
                  For emergent bilinguals and dyslexic students, the chunked lesson flow
                  and feedback made success more accessible.
                </WhyItMattersText>
              </WhyItMattersCard>
              <WhyItMattersCard>
                <WhyItMattersIcon>🧑‍🏫</WhyItMattersIcon>
                <WhyItMattersTitle>For Teachers</WhyItMattersTitle>
                <WhyItMattersText>
                  Less lecturing, more movement, and better connections with every student
                  in the room.
                </WhyItMattersText>
              </WhyItMattersCard>
              <WhyItMattersCard>
                <WhyItMattersIcon>🏫</WhyItMattersIcon>
                <WhyItMattersTitle>For Administrators</WhyItMattersTitle>
                <WhyItMattersText>
                  Confidence that high expectations and high support could coexist in
                  every classroom.
                </WhyItMattersText>
              </WhyItMattersCard>
            </WhyItMattersGrid>

            <PeoplePhotoRow>
              <PhotoItem>
                <CircularImageWrap $size={110}>
                  <Image
                    src="/case-study-flour-bluff/classroom.jpg"
                    layout="fill"
                    objectFit="cover"
                    objectPosition="center"
                    alt="Flour Bluff classroom"
                  />
                </CircularImageWrap>
                <PhotoLabel>The classroom</PhotoLabel>
              </PhotoItem>
              <PhotoItem>
                <CircularImageWrap $size={110}>
                  <Image
                    src="/case-study-flour-bluff/student-detail.jpg"
                    layout="fill"
                    objectFit="cover"
                    objectPosition="center top"
                    alt="Student working"
                  />
                </CircularImageWrap>
                <PhotoLabel>Student spotlight</PhotoLabel>
              </PhotoItem>
            </PeoplePhotoRow>

            <FinalQuoteCard>
              <QuoteMark>&ldquo;</QuoteMark>
              <FinalQuoteText>
                Curipod is a tool that takes a lot of guesswork out of so many aspects… It
                really does support every single aspect of what we&apos;re teaching.
                Beyond the test prep… it&apos;s invaluable.
              </FinalQuoteText>
              <QuoteAttrib>— Lisl Ames, 4th Grade Teacher</QuoteAttrib>
            </FinalQuoteCard>

            <TagRow>
              {['Differentiation', 'Feedback', 'Small Group', 'SPED'].map(tag => (
                <Tag key={tag}>{tag}</Tag>
              ))}
            </TagRow>

            <GetStartedWrapper>
              <Link href={isAuthenticated ? '/' : '/signup'} passHref>
                <a>
                  <Button
                    type="orange"
                    styles={{
                      borderRadius: '20px',
                      padding: '0 36px',
                      fontSize: 18,
                      height: 52,
                    }}
                  >
                    Get started for free
                  </Button>
                </a>
              </Link>
            </GetStartedWrapper>
          </CenteredSection>
        </MaxWidthContainer>
      </Section>

      {/* DOWNLOAD */}
      <DownloadSection>
        <MaxWidthContainer>
          <DownloadInner>
            <DownloadIcon aria-hidden="true">
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
            </DownloadIcon>
            <DownloadText>
              <DownloadTitle>Download the Case Study</DownloadTitle>
              <DownloadSubtitle>
                Get the full PDF version of the Flour Bluff case study.
              </DownloadSubtitle>
            </DownloadText>
            <a
              href="/Curipod_Case_Study_Flour_Bluff_ISD.pdf"
              download="Curipod_Case_Study_Flour_Bluff_ISD.pdf"
            >
              <Button
                type="tertiary"
                styles={{ borderRadius: '20px', padding: '0 28px', height: 48 }}
              >
                Download PDF
              </Button>
            </a>
          </DownloadInner>
        </MaxWidthContainer>
      </DownloadSection>
    </LandingPageLayout>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  return {
    props: {
      _tolgeeSSR: {
        language: DefaultLocale,
      },
    },
  };
};

export default FlourBluffCaseStudy;

/* ─── HERO ─────────────────────────────────────────────────────────────────── */

const HeroSection = styled.div`
  background: linear-gradient(
    135deg,
    ${theme.colors.orange} 0%,
    ${theme.colors.orangeDark} 100%
  );
  width: 100%;
  overflow: hidden;
  position: relative;

  &::before,
  &::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.08);
    pointer-events: none;
  }

  &::before {
    width: 600px;
    height: 600px;
    top: -180px;
    right: -120px;
  }

  &::after {
    width: 400px;
    height: 400px;
    bottom: -80px;
    left: -100px;
  }
`;

const HeroContent = styled.div`
  padding: 100px 20px 60px;
  color: ${theme.colors.white};

  @media ${MOBILE} {
    padding: 80px 20px 40px;
  }
`;

const HeroTag = styled.span`
  display: inline-block;
  background: rgba(255, 255, 255, 0.2);
  color: ${theme.colors.white};
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 2px;
  padding: 6px 14px;
  border-radius: 20px;
  margin-bottom: 20px;
`;

const HeroTitle = styled.h1`
  font-size: 72px;
  color: ${theme.colors.white};
  margin: 0 0 12px;
  line-height: 1.1;

  @media ${MOBILE} {
    font-size: 48px;
  }

  @media (max-width: 320px) {
    font-size: 36px;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 28px;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 600;
  margin: 0 0 20px;

  @media ${MOBILE} {
    font-size: 22px;
  }
`;

const HeroDescription = styled.p`
  font-size: 18px;
  color: rgba(255, 255, 255, 0.85);
  font-weight: 400;
  max-width: 640px;
  line-height: 1.7;
  margin: 0 0 32px;

  @media ${MOBILE} {
    font-size: 15px;
  }
`;

const HeroMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const MetaBadge = styled.span`
  background: rgba(255, 255, 255, 0.15);
  color: ${theme.colors.white};
  font-size: 13px;
  font-weight: 600;
  padding: 6px 14px;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.3);
`;

const HeroCtaWrapper = styled.div`
  margin-top: 36px;
`;

const HeroCtaButton = styled(Button)`
  background-color: ${theme.colors.white};
  color: ${theme.colors.orangeDark};
  border-radius: 28px;
  padding: 0 48px;
  font-size: 20px;
  height: 58px;
  font-weight: 800;
  letter-spacing: 0.3px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.18), 0 0 0 4px rgba(255, 255, 255, 0.25);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px) scale(1.03);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.22), 0 0 0 5px rgba(255, 255, 255, 0.35);
    background-color: ${theme.colors.white};
    color: ${theme.colors.orangeDark};
  }

  @media ${MOBILE} {
    font-size: 18px;
    height: 52px;
    padding: 0 36px;
  }
`;

const StatBannerWrapper = styled.div`
  background: rgba(0, 0, 0, 0.28);
  width: 100%;
  padding: 40px 0;
`;

const StatBannerInner = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  padding: 0 20px;

  @media ${MOBILE} {
    grid-template-columns: 1fr;
    gap: 32px;
  }
`;

const StatGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  justify-content: center;
  flex-wrap: nowrap;

  @media ${MOBILE} {
    flex-wrap: wrap;
    gap: 12px;
  }
`;

const StatDivider = styled.div`
  width: 1px;
  height: 80px;
  background: rgba(255, 255, 255, 0.3);
  margin: 0 28px;
  justify-self: center;
  flex-shrink: 0;

  @media ${MOBILE} {
    width: 60px;
    height: 1px;
    margin: 0 auto;
  }
`;

const StatItem = styled.div<{ $highlight?: boolean }>`
  text-align: center;
  padding: 10px 14px;
  border-radius: 10px;
  background: ${({ $highlight }) =>
    $highlight ? 'rgba(255, 255, 255, 0.15)' : 'transparent'};
  border: ${({ $highlight }) =>
    $highlight ? '1px solid rgba(255, 255, 255, 0.4)' : 'none'};
`;

const StatNumber = styled.div<{ $highlight?: boolean }>`
  font-size: 56px;
  font-weight: bold;
  color: ${theme.colors.white};
  line-height: 1;
  display: flex;
  align-items: flex-start;
  justify-content: center;

  @media ${MOBILE} {
    font-size: 44px;
  }
`;

const StatPercent = styled.span`
  font-size: 24px;
  margin-top: 6px;
`;

const StatLabel = styled.div`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.95);
  font-weight: 600;
  margin-top: 6px;
`;

const StatYear = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.82);
  margin-top: 2px;
`;

const ArrowContainer = styled.div`
  font-size: 36px;
  color: ${theme.colors.white};
  font-weight: bold;

  @media ${MOBILE} {
    font-size: 24px;
  }
`;

/* ─── DOWNLOAD CTA BANNER (below hero) ──────────────────────────────────────── */

const DownloadCtaBanner = styled.div`
  background: ${theme.colors.beigeDark};
  border-bottom: 1px solid rgba(249, 151, 70, 0.2);
  padding: 16px 0;
`;

const DownloadCtaInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  flex-wrap: wrap;

  @media ${MOBILE} {
    flex-direction: column;
    text-align: center;
    gap: 12px;
  }
`;

const DownloadCtaLabel = styled.span`
  font-size: 15px;
  font-weight: 500;
  color: ${theme.colors.text.dark};

  @media ${MOBILE} {
    font-size: 14px;
  }
`;

/* ─── SHARED SECTION PRIMITIVES ─────────────────────────────────────────────── */

const Section = styled.section<{ $bg: string }>`
  background-color: ${({ $bg }) => $bg};
  padding: 80px 0;

  @media ${MOBILE} {
    padding: 50px 0;
  }
`;

const SectionTag = styled.span`
  display: inline-block;
  color: ${theme.colors.orangeDark};
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 2px;
  margin-bottom: 12px;
`;

const SectionTitle = styled.h2`
  font-size: 40px;
  color: ${theme.colors.black};
  margin-bottom: 24px;
  line-height: 1.2;

  @media ${MOBILE} {
    font-size: 28px;
  }
`;

const SectionText = styled.p`
  font-size: 18px;
  color: ${theme.colors.text.dark};
  line-height: 1.75;
  font-weight: 400;
  margin-bottom: 20px;

  @media ${MOBILE} {
    font-size: 15px;
  }
`;

const CenteredSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

/* ─── CIRCULAR IMAGE ─────────────────────────────────────────────────────────── */

const CircularImageWrap = styled.div<{ $size: number; $borderColor?: string }>`
  position: relative;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  overflow: hidden;
  border: 3px solid ${({ $borderColor }) => $borderColor ?? theme.colors.orange};
  flex-shrink: 0;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
`;

/* ─── CHALLENGE SECTION ──────────────────────────────────────────────────────── */

const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: start;

  @media ${MOBILE} {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const BlockQuote = styled.blockquote`
  border-left: 4px solid ${theme.colors.orange};
  padding: 20px 24px;
  margin: 32px 0 0;
  background: rgba(249, 151, 70, 0.08);
  border-radius: 0 10px 10px 0;
`;

const QuoteMark = styled.span`
  display: block;
  font-size: 48px;
  color: ${theme.colors.orange};
  line-height: 0.8;
  margin-bottom: 8px;
`;

const QuoteText = styled.p`
  font-size: 22px;
  font-weight: 600;
  color: ${theme.colors.black};
  font-style: italic;
  line-height: 1.5;
  margin: 0 0 12px;

  @media ${MOBILE} {
    font-size: 18px;
  }
`;

const QuoteAttrib = styled.cite`
  font-size: 14px;
  font-weight: 600;
  color: ${theme.colors.orangeDark};
  font-style: normal;
  letter-spacing: 0.5px;
`;

const TeamCard = styled.div`
  background: ${theme.colors.white};
  border-radius: 16px;
  padding: 32px;
  box-shadow: ${theme.effects.magicFloatingShadow};

  @media ${MOBILE} {
    padding: 24px 20px;
  }
`;

const TeamPhotoArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 24px;
  border-bottom: 2px solid ${theme.colors.beigeDark};
`;

const TeamPhotoCaption = styled.p`
  font-size: 12px;
  color: ${theme.colors.text.dark};
  margin-top: 10px;
  font-style: italic;
  text-align: center;
`;

const TeamCardTitle = styled.h3`
  font-size: 20px;
  color: ${theme.colors.black};
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid ${theme.colors.orange};
`;

const TeamMemberList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const TeamMember = styled.div``;

const TeamRole = styled.div`
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 1.5px;
  color: ${theme.colors.orangeDark};
  text-transform: uppercase;
  margin-bottom: 2px;
`;

const TeamName = styled.div`
  font-size: 18px;
  font-weight: bold;
  color: ${theme.colors.black};
`;

const TeamNote = styled.div`
  font-size: 13px;
  color: ${theme.colors.text.dark};
  font-style: italic;
  margin-top: 2px;
`;

/* ─── FOCUS AREAS ────────────────────────────────────────────────────────────── */

const FocusAreaGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
`;

const FocusAreaBadge = styled.div`
  background: ${theme.colors.orange};
  color: ${theme.colors.white};
  font-size: 15px;
  font-weight: bold;
  padding: 10px 24px;
  border-radius: 30px;
`;

/* ─── IMPACT GRID ────────────────────────────────────────────────────────────── */

const ImpactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  margin-top: 40px;
  width: 100%;

  @media ${MOBILE} {
    grid-template-columns: 1fr;
  }
`;

const ImpactCard = styled.div<{ $wide?: boolean }>`
  background: ${theme.colors.white};
  border-radius: 16px;
  padding: 32px;
  box-shadow: ${theme.effects.defaultShadow};
  border-top: 4px solid ${theme.colors.orange};
  transition: ${theme.effects.defaultTransition};

  ${({ $wide }) =>
    $wide &&
    css`
      grid-column: 1 / -1;
    `}

  &:hover {
    box-shadow: ${theme.effects.magicFloatingShadowHovered};
    transform: translateY(-2px);
  }

  @media ${MOBILE} {
    padding: 24px 20px;
  }
`;

const ImpactIcon = styled.div`
  font-size: 32px;
  margin-bottom: 12px;
`;

const ImpactTitle = styled.h3`
  font-size: 20px;
  color: ${theme.colors.black};
  margin-bottom: 16px;
`;

const ImpactQuote = styled.p`
  font-size: 16px;
  color: ${theme.colors.text.dark};
  font-style: italic;
  line-height: 1.6;
  margin-bottom: 8px;
`;

const ImpactText = styled.p`
  font-size: 15px;
  color: ${theme.colors.text.dark};
  line-height: 1.6;
  margin-bottom: 8px;
`;

const ImpactAttrib = styled.cite`
  font-size: 13px;
  font-weight: bold;
  color: ${theme.colors.orangeDark};
  font-style: normal;
  letter-spacing: 0.5px;
`;

/* ─── RESULTS SECTION ────────────────────────────────────────────────────────── */

const ResultsSection = styled.section`
  background: linear-gradient(135deg, #1a1a2e 0%, ${theme.colors.black} 100%);
  padding: 80px 0;
  overflow: hidden;
  position: relative;

  &::before,
  &::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    background: rgba(249, 151, 70, 0.08);
    pointer-events: none;
  }

  &::before {
    width: 500px;
    height: 500px;
    top: -150px;
    right: -100px;
  }

  &::after {
    width: 350px;
    height: 350px;
    bottom: -80px;
    left: -80px;
  }

  @media ${MOBILE} {
    padding: 50px 0;
  }
`;

const ResultBlockQuote = styled.blockquote`
  border-left: 4px solid ${theme.colors.orange};
  padding: 20px 24px;
  margin: 0 0 48px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 0 10px 10px 0;
  max-width: 640px;
  width: 100%;
`;

const ResultNote = styled.p`
  font-size: 18px;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 400;
  max-width: 680px;
  line-height: 1.75;
  text-align: center;
  margin: 0 0 48px;

  strong {
    color: ${theme.colors.orange};
    font-weight: 700;
  }

  @media ${MOBILE} {
    font-size: 15px;
  }
`;

const ResultsStudentsPhoto = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
`;

const ResultsPhotoLabel = styled.p`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  font-style: italic;
  text-align: center;
  margin: 0;
`;

/* ─── WHY IT MATTERS ─────────────────────────────────────────────────────────── */

const WhyItMattersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  width: 100%;
  margin-bottom: 48px;

  @media ${MOBILE} {
    grid-template-columns: 1fr;
    gap: 16px;
    margin-bottom: 32px;
  }
`;

const WhyItMattersCard = styled.div`
  background: ${theme.colors.white};
  border-radius: 16px;
  padding: 28px 24px;
  text-align: center;
  box-shadow: ${theme.effects.defaultShadow};

  @media ${MOBILE} {
    padding: 20px 16px;
  }
`;

const WhyItMattersIcon = styled.div`
  font-size: 28px;
  margin-bottom: 12px;
`;

const WhyItMattersTitle = styled.h3`
  font-size: 17px;
  font-weight: bold;
  color: ${theme.colors.black};
  margin-bottom: 8px;
`;

const WhyItMattersText = styled.p`
  font-size: 15px;
  color: ${theme.colors.text.dark};
  line-height: 1.6;
  margin: 0;
`;

const PeoplePhotoRow = styled.div`
  display: flex;
  gap: 32px;
  justify-content: center;
  flex-wrap: wrap;
  margin-bottom: 48px;

  @media ${MOBILE} {
    gap: 20px;
    margin-bottom: 32px;
  }
`;

const PhotoItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const PhotoLabel = styled.p`
  font-size: 12px;
  color: ${theme.colors.text.dark};
  font-style: italic;
  text-align: center;
  margin: 0;
`;

const FinalQuoteCard = styled.div`
  background: ${theme.colors.white};
  border-radius: 16px;
  padding: 40px 48px;
  box-shadow: ${theme.effects.magicFloatingShadow};
  max-width: 680px;
  width: 100%;
  margin-bottom: 40px;

  @media ${MOBILE} {
    padding: 28px 20px;
  }
`;

const FinalQuoteText = styled.p`
  font-size: 28px;
  font-weight: 700;
  color: ${theme.colors.black};
  font-style: italic;
  line-height: 1.4;
  margin: 0 0 16px;

  @media ${MOBILE} {
    font-size: 22px;
  }
`;

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
`;

const Tag = styled.span`
  background: rgba(249, 151, 70, 0.12);
  color: ${theme.colors.orangeDark};
  font-size: 14px;
  font-weight: bold;
  padding: 8px 20px;
  border-radius: 20px;
  border: 1px solid ${theme.colors.orange};
`;

const GetStartedWrapper = styled.div`
  margin-top: 40px;
`;

/* ─── DOWNLOAD ───────────────────────────────────────────────────────────────── */

const DownloadSection = styled.section`
  background-color: ${theme.colors.beigeDark};
  border-top: 1px solid rgba(249, 151, 70, 0.25);
  padding: 32px 0;
`;

const DownloadInner = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;

  @media ${MOBILE} {
    flex-direction: column;
    text-align: center;
  }
`;

const DownloadIcon = styled.div`
  color: ${theme.colors.orangeDark};
  flex-shrink: 0;
  display: flex;
  align-items: center;

  @media ${MOBILE} {
    display: none;
  }
`;

const DownloadText = styled.div`
  flex: 1;
`;

const DownloadTitle = styled.p`
  font-size: 17px;
  font-weight: bold;
  color: ${theme.colors.black};
  margin: 0 0 2px;
`;

const DownloadSubtitle = styled.p`
  font-size: 14px;
  color: ${theme.colors.text.dark};
  font-weight: 400;
  margin: 0;
`;
